import React, { useState, useEffect } from 'react';
import { errorLogger } from '../../lib/error-logger';
import { supabase } from '../../lib/supabase';

export function ErrorDebugger() {
  const [errors, setErrors] = useState([]);
  const [showDebugger, setShowDebugger] = useState(false);
  const [authInfo, setAuthInfo] = useState(null);

  useEffect(() => {
    // Check for debug mode via URL or localStorage
    const urlParams = new URLSearchParams(window.location.search);
    const debugMode = urlParams.get('debug') === 'true' || localStorage.getItem('papersedge-debug') === 'true';
    
    if (debugMode) {
      setShowDebugger(true);
      loadDebugInfo();
    }

    // Refresh errors every 2 seconds when debugger is open
    let interval;
    if (showDebugger) {
      interval = setInterval(() => {
        setErrors([...errorLogger.getErrors()]);
      }, 2000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [showDebugger]);

  const loadDebugInfo = async () => {
    setErrors([...errorLogger.getErrors()]);
    
    // Get auth info
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      const session = await supabase.auth.getSession();
      
      setAuthInfo({
        user: user ? {
          id: user.id,
          email: user.email,
          created_at: user.created_at
        } : null,
        session: session.data.session ? {
          access_token: session.data.session.access_token ? 'present' : 'missing',
          refresh_token: session.data.session.refresh_token ? 'present' : 'missing',
          expires_at: session.data.session.expires_at
        } : null,
        error: error?.message
      });
    } catch (e) {
      setAuthInfo({ error: e.message });
    }
  };

  const toggleDebugger = () => {
    const newState = !showDebugger;
    setShowDebugger(newState);
    
    if (newState) {
      localStorage.setItem('papersedge-debug', 'true');
      loadDebugInfo();
    } else {
      localStorage.removeItem('papersedge-debug');
    }
  };

  const exportDebugData = () => {
    const debugData = {
      ...errorLogger.exportErrors(),
      authInfo,
      timestamp: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(debugData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `papersedge-debug-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!showDebugger) {
    return (
      <div style={{
        position: 'fixed',
        bottom: '10px',
        right: '10px',
        zIndex: 9999
      }}>
        <button
          onClick={toggleDebugger}
          style={{
            background: '#ff4444',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            fontSize: '20px',
            cursor: 'pointer'
          }}
        >
          🐛
        </button>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.9)',
      color: 'white',
      zIndex: 10000,
      overflow: 'auto',
      padding: '20px',
      fontFamily: 'monospace',
      fontSize: '12px'
    }}>
      <div style={{ marginBottom: '20px' }}>
        <button onClick={toggleDebugger} style={{ marginRight: '10px' }}>Close</button>
        <button onClick={() => errorLogger.clearErrors()} style={{ marginRight: '10px' }}>Clear Errors</button>
        <button onClick={exportDebugData} style={{ marginRight: '10px' }}>Export Debug Data</button>
        <button onClick={() => window.location.href = '/auth/callback'} style={{ marginRight: '10px' }}>Force Login</button>
        <button onClick={() => {
          localStorage.clear();
          sessionStorage.clear();
          window.location.reload();
        }} style={{ marginRight: '10px' }}>Clear All & Reload</button>
        <button onClick={async () => {
          try {
            // Force refresh the session
            const { data, error } = await supabase.auth.refreshSession();
            if (error) throw error;
            
            // Also try to set the session manually
            if (data?.session) {
              await supabase.auth.setSession(data.session);
            }
            
            alert('Session refreshed! Try reloading the page.');
            window.location.reload();
          } catch (e) {
            alert('Session refresh failed: ' + e.message);
          }
        }}>Fix Session</button>
      </div>

      <h3>Auth Info</h3>
      <pre style={{ background: '#333', padding: '10px', borderRadius: '5px' }}>
        {JSON.stringify(authInfo, null, 2)}
      </pre>

      <h3>Environment Info</h3>
      <pre style={{ background: '#333', padding: '10px', borderRadius: '5px' }}>
        {JSON.stringify({
          url: window.location.href,
          userAgent: navigator.userAgent,
          localStorage: errorLogger.getLocalStorageInfo(),
          serviceWorker: errorLogger.getServiceWorkerInfo()
        }, null, 2)}
      </pre>

      <h3>Recent Errors ({errors.length})</h3>
      {errors.length === 0 ? (
        <p>No errors logged</p>
      ) : (
        errors.map((error, index) => (
          <div key={error.id} style={{
            background: '#333',
            padding: '10px',
            marginBottom: '10px',
            borderRadius: '5px',
            borderLeft: '4px solid #ff4444'
          }}>
            <div style={{ fontWeight: 'bold', color: '#ff6666' }}>
              {error.type} - {new Date(error.timestamp).toLocaleTimeString()}
            </div>
            <pre style={{ marginTop: '5px', whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(error.details, null, 2)}
            </pre>
          </div>
        ))
      )}
    </div>
  );
}

// Hook to easily add debugging to any component
export function useDebugger() {
  const logError = (type, details) => {
    errorLogger.logError(type, details);
  };

  return { logError };
}